/*
 * Copyright (c) 2020, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: MIT
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/MIT
 */
import {
  getOwnPropertyDescriptor,
  defineProperty,
  getShadowRootFromHostElement,
  MapCreate,
  create,
} from '../shared/utils';
import {
  childrenGetter,
  innerHTMLGetter,
  outerHTMLGetter,
  childElementCountGetter,
  firstElementChildGetter,
  lastElementChildGetter,
  querySelector,
  querySelectorAll,
  getElementsByClassName,
  getElementsByTagName,
  getElementsByTagNameNS,
  matches,
  closest,
  addEventListener,
} from '../env/element';
import { createStaticHTMLCollection } from '../shared/static-html-collection';
import { getOuterHTML } from '../shared/outer-html';
import { arrayFromCollection, isHostElement } from '../shared/utils';
import { shadowRootGetter } from '../env/element';
import { getInnerHTML } from '../shared/inner-html';
import { isSlotElement } from './node';
import { extractEventMetadata } from './event';
import {
  querySelector as kagekiriQuerySelector,
  querySelectorAll as kagekiriQuerySelectorAll,
  getElementsByClassName as kagekiriGetElementsByClassName,
  getElementsByTagName as kagekiriGetElementsByTagName,
  getElementsByTagNameNS as kagekiriGetElementsByTagNameNS,
  matches as kagekiriMatches,
  closest as kagekiriClosest,
} from 'kagekiri';
import { createStaticNodeList } from '../shared/static-node-list';

const assignedSlotGetter = getOwnPropertyDescriptor(Element.prototype, 'assignedSlot')!.get!;

const fragmentChildrenGetter: (this: ParentNode) => HTMLCollectionOf<Element> = getOwnPropertyDescriptor(
  DocumentFragment.prototype,
  'children'
)!.get!;

export function getFilteredChildren(elm: Element): Element[] {
  if (isSlotElement(elm)) {
    return elm.assignedElements({ flatten: true });
  } else if (isHostElement(elm)) {
    return arrayFromCollection(fragmentChildrenGetter.call(getShadowRootFromHostElement(elm as HTMLElement)!));
  }
  return arrayFromCollection(childrenGetter.call(elm));
}

// Deep-traversing patches from this point on:

// The following patched methods surfaces shadowed elements in global
// traversing mechanisms.
const querySelectorDistortion = function querySelector(this: Element, selectors: string) {
  return kagekiriQuerySelector(selectors, this);
};

const querySelectorAllDistortion = function querySelectorAll(this: Element, selectors: string) {
  const elements = kagekiriQuerySelectorAll(selectors, this);
  return createStaticNodeList(elements);
};

const getElementsByClassNameDistortion = function getElementsByClassName(this: Element, classNames: string) {
  const elements = kagekiriGetElementsByClassName(classNames, this);
  return createStaticNodeList(elements);
};

const getElementsByTagNameDistortion = function getElementsByTagName(this: Element, tagName: string) {
  const elements = kagekiriGetElementsByTagName(tagName, this);
  return createStaticNodeList(elements);
};

const getElementsByTagNameNSDistortion = function getElementsByTagNameNS(
  this: Element,
  namespaceURI: string,
  tagName: string
) {
  const elements = kagekiriGetElementsByTagNameNS(namespaceURI, tagName, this);
  return createStaticNodeList(elements);
};

const matchesDistortion = function matches(this: Element, selector: string) {
  return kagekiriMatches(selector, this);
};

const closestDistortion = function closest(this: Element, selector: string) {
  return kagekiriClosest(selector, this);
};

// Non-deep-traversing patches: this descriptor map includes all descriptors that
// do not have access to nodes beyond the immediate children.
const innerHTMLDistortion = function innerHTML(this: Element): string {
  return getInnerHTML(this);
};

const outerHTMLDistortion = function outerHTML(this: Element): string {
  return getOuterHTML(this);
};

const shadowRootDistortion = function shadowRoot() {
  // you should not see any shadowRoot ever in this virtualization mode
  return null;
};

const childrenDistortion = function children(this: Element): HTMLCollectionOf<Element> {
  return createStaticHTMLCollection(getFilteredChildren(this));
};

const childElementCountDistortion = function childElementCount(this: Element): number {
  return getFilteredChildren(this).length;
};

const firstElementChildDistortion = function firstElementChild(this: Element): Element | null {
  return getFilteredChildren(this)[0];
};

const lastElementChildDistortion = function lastElementChild(this: Element): Element | null {
  const elements = getFilteredChildren(this);
  return elements[elements.length - 1];
};

const assignedSlotDistortion = function assignedSlot() {
  // you should now see any slotting ever in this virtualization mode
  return null;
};

export default MapCreate([
  [innerHTMLGetter, innerHTMLDistortion],
  [outerHTMLGetter, outerHTMLDistortion],
  [shadowRootGetter, shadowRootDistortion],
  [childrenGetter, childrenDistortion],
  [childElementCountGetter, childElementCountDistortion],
  [firstElementChildGetter, firstElementChildDistortion],
  [lastElementChildGetter, lastElementChildDistortion],
  [assignedSlotGetter, assignedSlotDistortion],

  [querySelector, querySelectorDistortion],
  [querySelectorAll, querySelectorAllDistortion],
  [getElementsByClassName, getElementsByClassNameDistortion],
  [getElementsByTagName, getElementsByTagNameDistortion],
  [getElementsByTagNameNS, getElementsByTagNameNSDistortion],
  [matches, matchesDistortion],
  [closest, closestDistortion],
]);

// Main Window Patches
const { attachShadow: originalAttachShadow } = Element.prototype;

// the role of this callback is to simply capture important events
// from `getObservedEventNames()` before they cross the first shadow boundary
// to capture the original event's metadata in case the event is observed
// from inside the oasis' realm.
function trackShadowEvent(e: Event) {
  extractEventMetadata(e);
}

const attachShadowPatched = function attachShadow(this: Element, options: ShadowRootInit): ShadowRoot {
  const sr = originalAttachShadow.call(this, options);
  // observed events needs to be captured for flattening them
  getObservedEventNames().forEach((name) => addEventListener.call(sr, name, trackShadowEvent));
  return sr;
};

defineProperty(Element.prototype, 'attachShadow', {
  value: attachShadowPatched,
  enumerable: true,
  writable: true,
  configurable: true,
});

function getDocumentChildrenWithShadows(): Element[] {
  // It is fine... it is slow... but this only should happen
  // few times, again, it is fine!
  return Array.from(document.querySelectorAll(`*`)).filter((elm: Element) => isHostElement(elm));
}

// returns all the active host elements with a shadow root
// attached to them
export function getAllActiveHostElements(): Map<Element, ShadowRoot> {
  const map = new Map<Element, ShadowRoot>();
  let currentSegment = getDocumentChildrenWithShadows();
  while (currentSegment.length > 0) {
    const elm = currentSegment.shift()!;
    // there is a possibility that with synthetic shadow we get some duplicate entries here,
    // we can simply about duplicates by checking the already defined map.
    if (!map.has(elm)) {
      const sr = getShadowRootFromHostElement(elm as HTMLElement);
      if (sr) {
        map.set(elm, sr);
        const newSegment: Element[] = Array.from(sr.querySelectorAll(`*`)).filter((elm: Element) => isHostElement(elm));
        currentSegment = currentSegment.concat(newSegment);
      }
    }
  }
  return map;
}

const observedEventNames: Record<string, 1> = create(null);

function getObservedEventNames() {
  return Reflect.ownKeys(observedEventNames);
}

export function addKnownEventName(name: string) {
  if (observedEventNames[name]) {
    return;
  }
  observedEventNames[name] = 1;
  getAllActiveHostElements().forEach((sr) => {
    addEventListener.call(sr, name, trackShadowEvent);
  });
}
